import type { GraphNode, NodeStatus } from '../../types/transit'

export function nodeStatusLabel(status: NodeStatus): string {
  switch (status) {
    case 'blocked': return 'Waiting'
    case 'ready': return 'Ready'
    case 'drafting': return 'AI drafting'
    case 'needs_input': return 'Needs input'
    case 'review': return 'Review'
    case 'done': return 'Done'
    case 'failed': return 'Failed'
  }
}

/** Where a section stands, in a few words (the section menu's subtext). open:
 *  its questions still to answer. */
export function nodeStatusDetail(status: NodeStatus, open = 0): string {
  switch (status) {
    case 'blocked': return 'Waiting on earlier sections to be approved'
    case 'ready': return 'Ready to draft'
    case 'drafting': return 'Overmatch is drafting it now'
    case 'needs_input': return open > 0 ? `Waiting on you: ${open} question${open === 1 ? '' : 's'} to answer` : 'Waiting on your answers'
    case 'review': return 'Drafted: read it, then approve it'
    case 'done': return 'Approved'
    case 'failed': return 'The draft failed: draft it again'
  }
}

/** A section's status as a chip, in the Overmatch Design System's tones:
 *  done and needs input as its Status chip (Complete, In progress), review
 *  in the accent (the user's turn), failed as an error Tag, the rest neutral. */
export function nodeChipClass(status: NodeStatus): string {
  switch (status) {
    case 'done': return 'bg-status-done text-status-foreground'
    case 'needs_input': return 'bg-status-attention text-status-foreground'
    case 'review': return 'bg-state-accent-subtle text-state-accent-text'
    case 'failed': return 'bg-state-error-subtle text-state-error-text'
    case 'drafting':
    case 'ready': return 'bg-secondary text-muted-foreground'
    default: return 'bg-secondary text-tertiary'
  }
}

/** "3m ago", "2h ago", "4d ago". */
export function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

/** Fired after a transition is created or the org changes, so the sidebar reloads. */
export const TRANSIT_CHANGED = 'transitChanged'

/** Fired with each graph the workspace loads (detail: TransitGraphEvent), so
 *  the sidebar's row for that transition shows the same section statuses
 *  without fetching them again. */
export const TRANSIT_GRAPH = 'transitGraph'
export interface TransitGraphEvent { transitionId: string; nodes: GraphNode[] }
